import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { supabase } from '@/lib/supabase'
import { VersionHistoryDialogProps } from '../../utils/contentTypes'
import { Database } from '../../../../../database.types'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"

/**
 * Dialog to display version history and allow restoring previous versions
 */
export function VersionHistoryDialog({
    contentId,
    isOpen,
    onClose,
    onViewVersion,
    initialVersion,
    onRevert
}: VersionHistoryDialogProps) {
    const [versions, setVersions] = useState<Array<{
        version_number: number
        name: string
        created_at: string
        change_summary: string | null
        is_current: boolean
        created_by: string | null
        status: Database['public']['Enums']['enum_content_status']
    }>>([])

    useEffect(() => {
        async function loadVersions() {
            const { data, error } = await supabase
                .from('content_versions')
                .select(`
                    version_number,
                    name,
                    created_at,
                    change_summary,
                    is_current,
                    created_by,
                    status
                `)
                .eq('content_id', contentId)
                .order('version_number', { ascending: false })

            if (data && !error) {
                setVersions(data)
            }
        }

        if (isOpen) {
            loadVersions()
        }
    }, [contentId, isOpen])

    useEffect(() => {
        if (isOpen && initialVersion) {
            onViewVersion(initialVersion)
            onClose()
        }
    }, [isOpen, initialVersion, onViewVersion, onClose])

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Version History</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    {versions.map(version => (
                        <div key={version.version_number} className="p-4 border rounded-lg bg-card">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h4 className="font-medium">Version {version.version_number}</h4>
                                        {version.is_current && (
                                            <Badge variant="secondary">Current</Badge>
                                        )}
                                    </div>
                                    {version.change_summary && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {version.change_summary}
                                        </p>
                                    )}
                                    <time className="text-sm text-muted-foreground block mt-2">
                                        {new Date(version.created_at).toLocaleString()}
                                    </time>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            onViewVersion(version.version_number)
                                            onClose()
                                        }}
                                    >
                                        View
                                    </Button>
                                    {!version.is_current && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => onRevert(version.version_number)}
                                        >
                                            Restore
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    )
}